import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { RutterLineItemMatchConfidence } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { memo, useEffect, useMemo, useState } from 'react';

import { QuickBooksItemLine, RutterLineItemMatch } from '~hooks/useRutter/models';
import { QuickBooksItemOption } from '~hooks/useRutter/useSettlementQuickBooksItems';
import getMappingSelectWidthPx from '~pages/Settings/Integrations/QuickBooksMapping/getMappingSelectWidthPx';
import MappingSelect from '~pages/Settings/Integrations/QuickBooksMapping/MappingSelect';
import { Nullable } from '~types/Nullable';

export interface QuickBooksItemColumn {
  areMatchesLoaded: boolean;
  isMatching: boolean;
  isReadOnly: boolean;
  itemOptions: QuickBooksItemOption[];
  matchesByAddOnChargeId: Map<string, RutterLineItemMatch[]>;
  matchesBySettlementLineItemId: Map<string, RutterLineItemMatch>;
  onRunMatching: () => Promise<void>;
  onSelect: (
    matches: RutterLineItemMatch[],
    lines: QuickBooksItemLine[],
    option: Nullable<QuickBooksItemOption>,
  ) => Promise<boolean>;
}

interface QuickBooksItemCellProps {
  areMatchesLoaded: boolean;
  isMatching: boolean;
  isReadOnly: boolean;
  itemOptions: QuickBooksItemOption[];
  lines: QuickBooksItemLine[];
  matches: RutterLineItemMatch[];
  onRunMatching: () => Promise<void>;
  onSelect: (
    matches: RutterLineItemMatch[],
    lines: QuickBooksItemLine[],
    option: Nullable<QuickBooksItemOption>,
  ) => Promise<boolean>;
}

function toOption(item: NonNullable<RutterLineItemMatch['rutterItem']>) {
  return { id: item.id, name: item.rutterApiItem?.name ?? '' };
}

const confidenceLabelKey = {
  [RutterLineItemMatchConfidence.HIGH]:
    'approvals.driver_pay.settlements.quick_books_item_confidence_high',
  [RutterLineItemMatchConfidence.MEDIUM]:
    'approvals.driver_pay.settlements.quick_books_item_confidence_medium',
  [RutterLineItemMatchConfidence.LOW]:
    'approvals.driver_pay.settlements.quick_books_item_confidence_low',
} satisfies Record<RutterLineItemMatchConfidence, string>;

// A match with no level (a direct mapping or an older match) keeps the plain sparkle.
const aiMarkerByConfidence = {
  [RutterLineItemMatchConfidence.HIGH]: { Icon: AutoAwesomeIcon, color: 'success.main' },
  [RutterLineItemMatchConfidence.MEDIUM]: {
    Icon: AutoAwesomeIcon,
    color: 'warning.main',
  },
  [RutterLineItemMatchConfidence.LOW]: { Icon: WarningAmberIcon, color: 'warning.main' },
} satisfies Record<
  RutterLineItemMatchConfidence,
  { Icon: typeof AutoAwesomeIcon; color: string }
>;

function MatchExplanation({
  lead,
  match,
}: {
  lead?: string;
  match: Nullable<RutterLineItemMatch>;
}) {
  const confidence =
    match?.confidence && !lead
      ? t('approvals.driver_pay.settlements.quick_books_item_confidence', {
          level: t(confidenceLabelKey[match.confidence]),
        })
      : null;
  const issues = match?.issues ?? [];

  return (
    <>
      <Typography component="div" variant="caption">
        {[lead && `${lead}.`, confidence, match?.reasoning].filter(Boolean).join(' ')}
      </Typography>
      {issues.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
          {issues.map((issue) => (
            <li key={issue}>
              <Typography component="span" variant="caption">
                {issue}
              </Typography>
            </li>
          ))}
        </Box>
      )}
    </>
  );
}

function QuickBooksItemCellComponent({
  areMatchesLoaded,
  isMatching,
  isReadOnly,
  itemOptions,
  lines,
  matches,
  onRunMatching,
  onSelect,
}: QuickBooksItemCellProps) {
  const [hasRequestedRun, setHasRequestedRun] = useState(false);
  const [didRunFindNothing, setDidRunFindNothing] = useState(false);

  const matchedItemIds = new Set(
    matches.flatMap((match) => (match.rutterItemId ? [match.rutterItemId] : [])),
  );
  const isMixed = matchedItemIds.size > 1;
  const sharedItem = matches.find((match) => match.rutterItem)?.rutterItem ?? null;
  const aiSuggestedMatch = matches.find((match) => match.isAiSuggested);

  // A row that combined several lines which matched different items offers only those
  // items; any other row offers every imported item.
  const options = useMemo(() => {
    if (isMixed) {
      const matchedItems = matches.flatMap((match) =>
        match.rutterItem ? [toOption(match.rutterItem)] : [],
      );

      return matchedItems.filter(
        (option, index) =>
          matchedItems.findIndex((other) => other.id === option.id) === index,
      );
    }

    const missingMatchedOptions = matches
      .flatMap((match) => (match.rutterItem ? [toOption(match.rutterItem)] : []))
      .filter((option) => !itemOptions.some((other) => other.id === option.id));

    return [...itemOptions, ...missingMatchedOptions];
  }, [isMixed, itemOptions, matches]);

  const selectWidthPx = getMappingSelectWidthPx(options);

  const matchedValue = !isMixed && sharedItem ? toOption(sharedItem) : null;
  const value = matchedValue;
  // The matcher said why it found no item; a line the user cleared carries no explanation.
  const matcherExplainedNoItem = matches.some(
    (match) => match.reasoning || match.issues.length > 0,
  );
  const hasNoMatch =
    areMatchesLoaded &&
    !isMatching &&
    !isMixed &&
    !value &&
    (didRunFindNothing || matcherExplainedNoItem);
  const { Icon: AiMarkerIcon, color: aiMarkerColor } = aiSuggestedMatch?.confidence
    ? aiMarkerByConfidence[aiSuggestedMatch.confidence]
    : { Icon: AutoAwesomeIcon, color: 'primary.main' };
  const canRunMatcher =
    areMatchesLoaded && !isReadOnly && !isMixed && !value && !hasNoMatch;

  const placeholder = isMixed
    ? t('approvals.driver_pay.settlements.quick_books_item_mixed')
    : isMatching && !value
      ? t('approvals.driver_pay.settlements.quick_books_item_matching')
      : t('approvals.driver_pay.settlements.quick_books_item_select');

  // Once a run the user started finishes without an item for this row, flag it.
  useEffect(() => {
    if (!hasRequestedRun || isMatching) {
      return;
    }

    setHasRequestedRun(false);

    setDidRunFindNothing(!value);
  }, [hasRequestedRun, isMatching, value]);

  const handleRunMatcher = () => {
    setHasRequestedRun(true);
    void onRunMatching();
  };

  const handleSave = (option: Nullable<QuickBooksItemOption>) => {
    setDidRunFindNothing(false);

    return onSelect(matches, lines, option);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      {/* wide enough to read an item name; the icons never shrink into it */}
      <Box sx={{ flexShrink: 0, width: selectWidthPx }}>
        <MappingSelect
          dataTestId="settlement-quick-books-item-select"
          disabled={isReadOnly || !areMatchesLoaded}
          onSave={handleSave}
          options={options}
          placeholder={placeholder}
          value={value}
          width={selectWidthPx}
        />
      </Box>
      {aiSuggestedMatch && value && (
        <Tooltip title={<MatchExplanation match={aiSuggestedMatch} />}>
          <AiMarkerIcon
            data-confidence={aiSuggestedMatch.confidence ?? undefined}
            data-test-id="settlement-quick-books-item-ai-icon"
            fontSize="small"
            sx={{ color: aiMarkerColor, flexShrink: 0 }}
          />
        </Tooltip>
      )}
      {canRunMatcher && (
        <Tooltip
          title={t('approvals.driver_pay.settlements.quick_books_item_find_match')}
        >
          <span>
            <IconButton
              aria-label={t(
                'approvals.driver_pay.settlements.quick_books_item_find_match',
              )}
              data-test-id="settlement-quick-books-item-run-matcher"
              disabled={isMatching}
              onClick={handleRunMatcher}
              size="small"
              sx={{ color: 'primary.main', flexShrink: 0 }}
            >
              {isMatching ? (
                <CircularProgress size={18} />
              ) : (
                <AutoAwesomeIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {hasNoMatch && (
        <Tooltip
          title={
            <MatchExplanation
              lead={t('approvals.driver_pay.settlements.quick_books_item_no_match_found')}
              match={matches[0] ?? null}
            />
          }
        >
          <WarningAmberIcon
            data-test-id="settlement-quick-books-item-no-match"
            fontSize="small"
            sx={{ color: 'warning.main', flexShrink: 0 }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

// Callers build `matches` and `lines` inline, so compare them by content, not by array identity.
export const QuickBooksItemCell = memo(
  QuickBooksItemCellComponent,
  (previous, next) =>
    previous.matches.length === next.matches.length &&
    previous.matches.every((match, index) => match === next.matches[index]) &&
    previous.lines.length === next.lines.length &&
    previous.lines.every(
      (line, index) =>
        line.addOnChargeId === next.lines[index].addOnChargeId &&
        line.settlementLineItemId === next.lines[index].settlementLineItemId,
    ) &&
    (Object.keys(previous) as (keyof QuickBooksItemCellProps)[]).every(
      (key) => key === 'matches' || key === 'lines' || previous[key] === next[key],
    ),
);
